import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useIntl } from "react-intl";
import IndicatorActions from "../IndicatorActions";
import { formatIndicatorLabel } from "../../helpers/indicatorLabel";
import "./IndicatorLegend.sass";

const TOGGLE_ICON_SIZE = 14;

/**
 * Legend of the indicators on the price chart, under the clock
 * (po-chart-visual-spec.md §5.2): a row "Name parameters" 👁 ✏️ ✕ per
 * indicator, a hidden one muted; ◂ hides the rows together with the pane
 * headers (one switch for all titles). Not shown without indicators.
 *
 * @param {object}   props
 * @param {object[]} props.indicators - engine.getIndicators() items over the price (not oscillators)
 * @param {boolean}  props.areTitlesHidden
 * @param {() => void} props.onToggleTitles
 * @param {(key: string, isVisible: boolean) => void} props.onSetVisible
 * @param {(key: string) => void} [props.onEdit] - ✏️ is disabled without it
 * @param {(key: string) => void} props.onRemove
 */
const IndicatorLegend = ({
  indicators,
  areTitlesHidden: isCollapsed,
  onToggleTitles,
  onSetVisible,
  onEdit,
  onRemove,
}) => {
  const intl = useIntl();

  if (!indicators.length) return null;

  const toggleLabel = intl.formatMessage({
    id: isCollapsed ? "futures.chart.indicators.legend_expand" : "futures.chart.indicators.legend_collapse",
  });
  const ToggleIcon = isCollapsed ? ChevronRight : ChevronLeft;

  return (
    <div className="indicator-legend">
      <button
        type="button"
        className="indicator-legend__toggle"
        aria-label={toggleLabel}
        title={toggleLabel}
        aria-expanded={!isCollapsed}
        onClick={onToggleTitles}
      >
        <ToggleIcon size={TOGGLE_ICON_SIZE} aria-hidden="true" />
      </button>
      {!isCollapsed && (
        <ul className="indicator-legend__rows">
          {indicators.map((indicator) => {
            const { key, isVisible } = indicator;
            const label = formatIndicatorLabel(indicator);

            return (
              <li
                key={key}
                className={`indicator-legend__row${isVisible ? "" : " indicator-legend__row--hidden"}`}
              >
                <span className="indicator-legend__label" title={label}>
                  {label}
                </span>
                <IndicatorActions
                  label={label}
                  isVisible={isVisible}
                  onToggleVisible={() => onSetVisible(key, !isVisible)}
                  onEdit={onEdit && (() => onEdit(key))}
                  onRemove={() => onRemove(key)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default IndicatorLegend;
